import React, {FC} from "react";
import {useTarif595Enabled} from "../../../hooks/useTarif595Enabled";
import {Menu, MenuProps} from "antd";
import {useHistory, useLocation} from "react-router-dom";
import classNames from "classnames";
import {ItemType} from "antd/lib/menu/hooks/useItems";
import {MainMenuProps, MenuNavigationItem} from "../types";
import css from "../LayoutStandard.less";
import Logout from "../../../containers/logout/Logout";
import {getMainMenuItems} from "../../../containers/common/MainMenuRoutes";
import {routePermissions} from "../../../containers/common/AuthenticateSetup";
import {URL_WITHDRAWALS} from "../../../models/url";
import {useGetCompanyInfoQuery, useSettingEndUserAppPublicQuery, useWithdrawalsQuery, WithdrawalStatus} from "../../../generated/graphql";
import {useHiddenStaffAreas} from "../../../hooks/useStaffVisibility";
import {useThrottledFetchPolicy} from "../../../hooks/useThrottledFetchPolicy";

const MainMenu: FC<MainMenuProps> = ({className, highestRole, rolesOfCurrent, isInstructor = false, menuTextClasses, closed, companyInfo, secondLevelNavigationItems, navigationIsOpen, isStage}) => {
    const history = useHistory();
    const location = useLocation();
    const mainMenuClasses = [css.mainMenu, className];
    const [openKeys, setOpenKeys] = React.useState<Array<string>>([]);
    const {data: companyInfoData} = useGetCompanyInfoQuery({
        // fetchPolicy: "network-only",
        fetchPolicy: "cache-first",
    });

    const featureWeekPlaner = companyInfoData?.companyInfo?.subscription?.featureWeekPlaner;
    const featurePrerequisiteCourses = companyInfoData?.companyInfo?.subscription?.featurePrerequisiteCourses;
    const featureDiscountCodes = companyInfoData?.companyInfo?.subscription?.featureDiscountCodes;
    const featureExportInstructorLessons = companyInfoData?.companyInfo?.subscription?.featureExportInstructorLessons;
    const featureStaffLogin = companyInfoData?.companyInfo?.subscription?.featureStaffLogin;
    const featureBillingStatus = companyInfoData?.companyInfo?.subscription?.featureBillingStatus;
    const featureAdminManagement = companyInfoData?.companyInfo?.subscription?.featureAdminManagement;
    const featureAttendanceListBulk = companyInfoData?.companyInfo?.subscription?.featureAttendanceListBulk;
    const featureExtendedBadges = companyInfoData?.companyInfo?.subscription?.featureExtendedBadges;
    const featureAreaManager = companyInfoData?.companyInfo?.subscription?.featureAreaManager;
    const featureDataExport = companyInfoData?.companyInfo?.subscription?.featureDataExport;
    const featureWebModulAdminAccess = companyInfoData?.companyInfo?.subscription?.featureWebModulAdminAccess;
    const featureMcp = companyInfoData?.companyInfo?.subscription?.featureMcp;
    // Phase 14 Plan 14-03 (D-18) — Refunds-Menu-Item Gate
    const featureMollie = !!companyInfoData?.companyInfo?.subscription?.featureMollie;
    const featureBankImport = !!companyInfoData?.companyInfo?.subscription?.featureBankImport;
    const tarif595Enabled = useTarif595Enabled();
    const featureInterestList = companyInfoData?.companyInfo?.subscription?.featureInterestList;
    const featureNewsletter = companyInfoData?.companyInfo?.subscription?.featureNewsletter;
    // #708/#709: Bereiche mit „Kein Zugriff“ ausblenden (bis die Regeln geladen sind: sichtbar).
    const hiddenAreas = useHiddenStaffAreas();

    // Offene Widerrufe für das global gerenderte Menü-Badge. Die Query feuert nur,
    // wenn der aktuelle Nutzer überhaupt Zugriff auf Widerrufe hat (sonst Forbidden
    // + unnötige Last). Per Apollo-Cache mit der Query in Withdrawals.tsx dedupliziert.
    const canSeeWithdrawals = routePermissions[URL_WITHDRAWALS].includes(highestRole);

    // Sichtbarkeit des Widerrufe-Menüeintrags: Kundenportal-Button aktiv ODER es
    // gibt offene Widerrufe. Ist der Button aus UND nichts offen, hat der Eintrag
    // keinen Zweck (und ein Admin kann das Setting ohnehin nicht selbst umschalten
    // — nur Superuser/Web-Modul-Freigabe). withdrawalButtonEnabled über die
    // UNGESPERRTE settingEndUserAppPublic-Query (kein assertWebModulAccess → kein
    // ForbiddenError).
    const {data: portalSettingsData} = useSettingEndUserAppPublicQuery({
        skip: !canSeeWithdrawals,
        fetchPolicy: useThrottledFetchPolicy("SettingEndUserAppPublic", 60 * 1000),
    });
    const withdrawalButtonEnabled =
        portalSettingsData?.settingEndUserAppPublic?.withdrawalButtonEnabled ?? false;

    // Offene Widerrufe (status "new") — fürs Menü-Badge UND als zweites
    // Sichtbarkeitskriterium: Der Eintrag bleibt sichtbar, solange etwas offen ist,
    // selbst wenn der Button inzwischen deaktiviert wurde. Daher NICHT auf den
    // Button-Status skippen.
    const {data: withdrawalsData} = useWithdrawalsQuery({
        skip: !canSeeWithdrawals,
        // Neue Widerrufe kommen von Endkunden: beim Navigieren nachladen, höchstens einmal pro Minute
        fetchPolicy: useThrottledFetchPolicy("Withdrawals", 60 * 1000),
    });
    const withdrawalsOpenCount = (withdrawalsData?.withdrawals ?? []).filter(
        (row) => row.status === WithdrawalStatus.New,
    ).length;

    const isMenuItemActive = (itemPath: string): boolean => {
        return location.pathname.startsWith(`${itemPath}/`) || location.pathname === itemPath;
    };

    const addAuxClick = (link: string) => {
        return (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (e.button === 1) {
                window.open(link, "_blank");
            }
        };
    };

    const handleMenuClick = (link: string, childLink?: string) => (e: any) => {
        const mouseEvent = e.domEvent as MouseEvent;
        const targetLink = childLink ?? link;

        // Für mittlere Maustaste oder Cmd/Ctrl+Click öffnen wir in neuem Tab
        if (mouseEvent.button === 1 || mouseEvent.ctrlKey || mouseEvent.metaKey) {
            window.open(targetLink, "_blank");

            return;
        }
        // Für normalen Klick nutzen wir die App-Navigation
        history.push(targetLink);
    };

    const mainMenuItems = (): MenuProps["items"] => {
        const items = Object.values(
            getMainMenuItems({
                highestRole,
                featureWeekPlaner,
                featurePrerequisiteCourses,
                featureDiscountCodes,
                featureExportInstructorLessons,
                featureStaffLogin,
                featureBillingStatus,
                featureAttendanceListBulk,
                featureExtendedBadges,
                featureAreaManager,
                featureDataExport,
                featureWebModulAdminAccess,
                featureMcp,
                featureMollie,
                featureBankImport,
                tarif595Enabled,
                featureInterestList,
                featureNewsletter,
                featureAdminManagement,
                withdrawalsOpenCount,
                withdrawalButtonEnabled,
                isInstructor,
                rolesOfCurrent,
                hiddenAreas,
            }),
        ).map((item) => ({
            ...item,
            onClick: handleMenuClick(item.link),
            onTitleClick: handleMenuClick(item.link),
            onAuxClick: addAuxClick(item.link),
            className: isMenuItemActive(item.link) ? (isStage ? classNames("ant-menu-item-selected", css.stage) : "ant-menu-item-selected") : "",
        }));

        const itemsWithChildren = items.map((item) => {
            if (!item.children || item.children.length <= 1) {
                return {
                    ...item,
                    children: undefined,
                    label: <span data-testid={item.key ?? ""}>{item.label}</span>,
                    onTitleClick: handleMenuClick(item.link, item.children?.length === 1 ? item.children[0].link : undefined),
                    onAuxClick: addAuxClick(item.children?.length === 1 ? item.children[0].link : item.link),
                };
            }

            return {
                ...item,
                label: <span data-testid={item.key ?? ""}>{item.label}</span>,
                popupClassName: isStage ? classNames(css.mainMenuAntPopup, css.stage) : css.mainMenuAntPopup,
                onTitleClick: handleMenuClick(item.link),
                onAuxClick: addAuxClick(item.link),
                children: item.children.map((child: MenuNavigationItem) => ({
                    ...child,
                    label: <span data-testid={child.key ?? ""}>{child.label}</span>,
                    expandIcon: child.children?.length < 2 ? <></> : undefined,
                    onTitleClick: handleMenuClick(child.link),
                    onAuxClick: addAuxClick(child.link),
                    className: isMenuItemActive(child.link) ? (isStage ? classNames("ant-menu-item-selected", css.stage) : "ant-menu-item-selected") : "",
                })),
            };
        });

        return itemsWithChildren;
    };

    return (
        <nav
            className={classNames(mainMenuClasses)}
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            <Menu
                style={{flex: 10}}
                items={mainMenuItems()}
                className={
                    navigationIsOpen
                        ? isStage
                            ? classNames(css.mainMenuAnt, css.stage)
                            : classNames(css.mainMenuAnt)
                        : isStage
                        ? classNames(css.mainMenuAnt, css.mainMenuAntClosed, css.stage)
                        : classNames(css.mainMenuAnt, css.mainMenuAntClosed)
                }
                theme="dark"
                forceSubMenuRender
                subMenuCloseDelay={0.2}
                openKeys={openKeys}
                selectedKeys={[location.pathname]}
                onOpenChange={(openKeys) => {
                    setOpenKeys(openKeys);
                }}
            />
            <div
                className={css.logout}
                style={{
                    flexGrow: 1,
                    borderTop: "1px solid rgba(255, 255, 255, 0.5)",
                }}
            >
                <Logout highestRole={highestRole} menuClosed={closed} />
            </div>
        </nav>
    );
};

export default MainMenu;
